import { useMemo, type JSX } from 'react';

/**
 * The decoration down both margins: a network of nodes, circuit traces, dot grids and glyphs.
 *
 * Drawn rather than loaded. A picture can only be moved and scaled; what was asked for is a
 * background that lives — nodes that breathe, signals that travel along the traces — and that
 * needs shapes the page can address one at a time. It also costs no download, survives any screen
 * shape, and can dim itself for the dark theme, none of which a flat image manages.
 *
 * The layout is random but not new each time. A fixed seed means the same picture on every visit
 * and every render: a decoration that rearranged itself whenever a page re-rendered would be
 * movement nobody asked for, at the edge of the eye, while somebody is trying to read.
 *
 * It lives in the margins on purpose. The middle is where the lesson is, and a background that
 * reaches under the text has stopped being one.
 */

/** The drawing's own coordinate space. Tall and narrow: it hugs an edge, whatever the screen. */
const WIDTH = 460;
const HEIGHT = 1080;

/** Enough to read as a network, few enough that the eye is not asked to follow it. */
const NODES = 18;

/** How many neighbours a node reaches for. Two makes threads; three starts making fabric. */
const LINKS_PER_NODE = 2;

/**
 * A small deterministic generator.
 *
 * `Math.random` would redraw the decoration on every render, which is the one thing a background
 * must never do. This gives the same sequence for the same seed, for ever.
 */
function seeded(seed: number): () => number {
  let state = seed >>> 0;

  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

interface Node {
  readonly x: number;
  readonly y: number;
  readonly r: number;
  /** A ring around it, as the brighter nodes in the artwork have. */
  readonly halo: boolean;
  /** The one accent colour, on perhaps a tenth of them. */
  readonly warm: boolean;
  readonly delay: number;
}

interface Drawing {
  readonly nodes: readonly Node[];
  readonly links: readonly { x1: number; y1: number; x2: number; y2: number }[];
  readonly traces: readonly { d: string; delay: number }[];
  readonly grids: readonly { x: number; y: number; cols: number; rows: number }[];
  readonly squares: readonly { x: number; y: number; size: number; filled: boolean; warm: boolean }[];
  readonly glyphs: readonly { x: number; y: number; text: string; size: number }[];
}

/**
 * Builds one margin's worth of decoration.
 *
 * Everything is generated against a left-hand edge and folded over for the right, which keeps one
 * set of rules for "near the edge" rather than two that drift apart.
 */
function draw(seed: number, mirror: boolean): Drawing {
  const random = seeded(seed);
  const flip = (x: number): number => (mirror ? WIDTH - x : x);

  /*
   * Nodes crowd the edge and thin out inward: squaring a number between nought and one bunches it
   * near nought. Without the bias the network spreads evenly across the margin and wanders in
   * under the text, which is where a decoration stops being one.
   */
  const nodes: Node[] = Array.from({ length: NODES }, (_, index) => {
    const bias = random() ** 2;

    return {
      x: flip(bias * (WIDTH - 60) + 10),
      y: ((index + random()) / NODES) * HEIGHT,
      r: 2.5 + random() * 5,
      halo: random() < 0.28,
      warm: random() < 0.18,
      delay: random() * 6,
    };
  });

  // Each node reaches for its nearest few. Distance alone makes the mesh, exactly as the eye reads
  // it: things near each other look connected whether or not anybody drew a line.
  const links: { x1: number; y1: number; x2: number; y2: number }[] = [];

  nodes.forEach((from, index) => {
    const near = nodes
      .map((to, other) => ({ to, other, d: Math.hypot(to.x - from.x, to.y - from.y) }))
      .filter((candidate) => candidate.other !== index)
      .sort((a, b) => a.d - b.d)
      .slice(0, LINKS_PER_NODE);

    for (const { to, other } of near) {
      // Each pair once: A reaching for B and B reaching for A is one line drawn twice.
      if (other > index) links.push({ x1: from.x, y1: from.y, x2: to.x, y2: to.y });
    }
  });

  /*
   * Traces: in from the edge, a step up or down, on a little further, stop. Right angles with
   * rounded joins, the way the artwork draws them — and the way a circuit board does.
   */
  const traces = Array.from({ length: 7 }, (_, index) => {
    const y = ((index + 0.5) / 7) * HEIGHT + (random() - 0.5) * 60;
    const first = 40 + random() * 90;
    const step = (random() < 0.5 ? -1 : 1) * (30 + random() * 50);
    const second = first + 50 + random() * 120;

    const points: [number, number][] = [
      [flip(0), y],
      [flip(first), y],
      [flip(first + Math.abs(step) * 0.6), y + step],
      [flip(second), y + step],
    ];

    const d = points.map(([x, py], at) => `${at === 0 ? 'M' : 'L'}${x} ${py}`).join(' ');

    return { d, delay: random() * 8 };
  });

  const grids = Array.from({ length: 3 }, (_, index) => ({
    x: flip(30 + random() * 180),
    y: ((index + 0.5) / 3) * HEIGHT + (random() - 0.5) * 120,
    cols: 4 + Math.floor(random() * 3),
    rows: 4 + Math.floor(random() * 2),
  }));

  /*
   * Squares, filled and outlined, warm and cool.
   *
   * Small, and half of them the accent colour. They are what stops the drawing reading as a plain
   * network diagram: circles alone are a graph, circles with squares are a circuit.
   */
  const squares = Array.from({ length: 7 }, () => ({
    x: flip(20 + random() ** 1.5 * 260),
    y: random() * HEIGHT,
    size: 5 + random() * 4,
    filled: random() < 0.5,
    warm: random() < 0.45,
  }));

  // Not folded over: a backwards brace is a mistake, not a decoration.
  const glyphs = [
    { x: 40 + random() * 120, y: random() * 200 + 60, text: '{ }', size: 34 },
    { x: 40 + random() * 140, y: 300 + random() * 200, text: '< >', size: 26 },
    { x: 40 + random() * 140, y: 620 + random() * 160, text: '{ }', size: 30 },
    { x: 40 + random() * 120, y: 860 + random() * 160, text: '+', size: 30 },
  ].map((glyph) => ({ ...glyph, x: flip(glyph.x) }));

  return { nodes, links, traces, grids, squares, glyphs };
}

/** One margin, drawn. */
function Side({ seed, mirror }: { seed: number; mirror: boolean }): JSX.Element {
  const art = useMemo(() => draw(seed, mirror), [seed, mirror]);

  return (
    <svg
      className={`backdrop__side backdrop__side--${mirror ? 'right' : 'left'}`}
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      preserveAspectRatio={`${mirror ? 'xMaxYMid' : 'xMinYMid'} slice`}
      aria-hidden="true"
      focusable="false"
    >
      <g className="backdrop__drift">
        <g className="backdrop__links">
          {art.links.map((link, index) => (
            <line key={index} x1={link.x1} y1={link.y1} x2={link.x2} y2={link.y2} />
          ))}
        </g>

        <g className="backdrop__traces">
          {art.traces.map((trace, index) => (
            <g key={index}>
              {/*
               * Three lines a few units apart rather than one. Traces on a board run in bundles,
               * and a single line reads as a stray scratch where a bundle reads as wiring.
               */}
              {[0, 6, 12].map((offset) => (
                <path key={offset} d={trace.d} transform={`translate(0 ${offset})`} />
              ))}
              {/*
               * The signal: the same path again, drawn as one short dash that runs along it.
               *
               * A shape moved by offset-path would be the obvious way and is not the portable
               * one — motion paths on SVG children still vary between browsers, and a signal that
               * fails to move sits in the corner as a stray square. A dash cannot be misplaced,
               * because it is the trace.
               */}
              <path
                className="backdrop__signal"
                d={trace.d}
                style={{ animationDelay: `${trace.delay}s` }}
              />
            </g>
          ))}
        </g>

        <g className="backdrop__grids">
          {art.grids.map((grid, index) => (
            <g key={index} style={{ animationDelay: `${index * 1.7}s` }}>
              {Array.from({ length: grid.cols * grid.rows }, (_, cell) => (
                <circle
                  key={cell}
                  cx={grid.x + (cell % grid.cols) * 11}
                  cy={grid.y + Math.floor(cell / grid.cols) * 11}
                  r="1.6"
                />
              ))}
            </g>
          ))}
        </g>

        <g className="backdrop__squares">
          {art.squares.map((square, index) => (
            <rect
              key={index}
              className={square.warm ? 'backdrop__square backdrop__square--warm' : 'backdrop__square'}
              x={square.x}
              y={square.y}
              width={square.size}
              height={square.size}
              fillOpacity={square.filled ? 1 : 0}
            />
          ))}
        </g>

        <g className="backdrop__nodes">
          {art.nodes.map((node, index) => (
            <g key={index} style={{ animationDelay: `${node.delay}s` }}>
              {node.halo ? (
                <circle className="backdrop__halo" cx={node.x} cy={node.y} r={node.r + 4} />
              ) : null}
              <circle
                className={node.warm ? 'backdrop__node backdrop__node--warm' : 'backdrop__node'}
                cx={node.x}
                cy={node.y}
                r={node.r}
              />
            </g>
          ))}
        </g>

        <g className="backdrop__glyphs">
          {art.glyphs.map((glyph, index) => (
            <text key={index} x={glyph.x} y={glyph.y} fontSize={glyph.size}>
              {glyph.text}
            </text>
          ))}
        </g>
      </g>
    </svg>
  );
}

/**
 * The page's backdrop, behind everything and reachable by nothing.
 *
 * Hidden from assistive technology and from the pointer: it carries no meaning and must never
 * catch a click meant for the page. Narrow screens get none of it — there are no margins left to
 * put it in, and behind the text it would only make the text harder to read.
 */
export function Backdrop(): JSX.Element {
  return (
    <div className="backdrop" aria-hidden="true">
      <Side seed={20260901} mirror={false} />
      <Side seed={77712345} mirror />
    </div>
  );
}
